import { useEffect, useMemo, useState } from "react";
import { useCookies } from "./CookieChecker.js";
import { useMasterIb } from "./MasterIbChecker.js";
import { GetDashboardData } from "./LocalStorage.js";
import TreeIbsRequest from "../Endpoints/TreeIbsRequest.js";

// Resolves the options for the "IB selector" shown on every IBs report tab:
//   - BROKER accounts: the IBs cached locally on login (dashboard data).
//   - Master IBs (see Tools/MasterIbChecker.js): the IBs that belong to
//     every tree where `is_master` is true, fetched via
//     POST /tree-reports/ibs.
//
// This is resolved ONCE here, in the parent IBsPage, and the result is
// shared by every tab (via props) - so switching between "Summarized
// Report" and "Full Report" never re-fetches this list or re-hits
// /tree-reports/ibs.
//
// IMPORTANT: `ibOptionsLoading` starts as `true` (not `false`) on purpose.
// Consumers (SummarizedReportTab/FullReportTab) only run their "select
// everything and fetch the default report" effect once `ibOptionsLoading`
// becomes `false` - if it started `false`, they could fire that effect on
// the very first render, while `ibOptions`/`individualEmails` are still
// empty (before the real list has loaded), permanently locking their
// "only run once" ref with an empty selection. That was the exact bug
// behind the selector showing only "All" checked (no individual IBs) and
// the table showing "No results found" even though the IB list loads
// correctly a moment later.
export function useIbOptions() {
  const { type } = useCookies();
  const { isMasterIb, masterTrees, checkingMasterIb } = useMasterIb();
  const canUseIbSelector = type === "BROKER" || type === "MASTER_IB" || isMasterIb;

  const [ibOptions, setIbOptions] = useState([]);
  const [ibOptionsLoading, setIbOptionsLoading] = useState(true);
  const [ibOptionsError, setIbOptionsError] = useState("");

  useEffect(() => {
    // `type` isn't resolved yet (shouldn't normally happen here, since
    // IBsPage only mounts after CookieChecker already resolved the
    // session, but guard anyway) - keep "loading" instead of deciding
    // anything final.
    if (!type) {
      setIbOptionsLoading(true);
      return undefined;
    }

    const isAdminType = type === "ADMIN" || type === "SUPERADMIN";
    const isIbLikeType = !isAdminType && type !== "BROKER" && type !== "MASTER_IB";

    // A plain "IB" cookie type might still turn out to be a Master IB once
    // Tools/MasterIbChecker.js finishes checking - keep "loading" instead of
    // settling on "no selector" while that's still unresolved.
    if (isIbLikeType && checkingMasterIb) {
      setIbOptionsLoading(true);
      return undefined;
    }

    if (!canUseIbSelector) {
      setIbOptions([]);
      setIbOptionsError("");
      setIbOptionsLoading(false);
      return undefined;
    }

    if (type === "BROKER" || type === "MASTER_IB") {
      const dataResponse = GetDashboardData();
      if (dataResponse.dataExits && dataResponse.ibs.length > 0) {
        const formatted = dataResponse.ibs.map((item) => ({
          label: item.name,
          value: item.email_ib,
        }));
        setIbOptions([{ label: "All", value: "All" }, ...formatted]);
      } else {
        setIbOptions([]);
      }
      setIbOptionsError("");
      setIbOptionsLoading(false);
      return undefined;
    }

    // Master IB path (resolved via `isMasterIb`, not the "type" cookie).
    const treeIds = (masterTrees || [])
      .map((tree) => tree?.id)
      .filter((treeId) => treeId !== null && treeId !== undefined);

    if (!treeIds.length) {
      setIbOptions([]);
      setIbOptionsError("");
      setIbOptionsLoading(false);
      return undefined;
    }

    let alive = true;
    setIbOptionsLoading(true);
    setIbOptionsError("");

    TreeIbsRequest({ tree_ids: treeIds })
      .then((response) => {
        if (!alive) return;

        const ibsList = Array.isArray(response?.data?.data) ? response.data.data : [];

        const seenEmails = new Set();
        const options = [];

        ibsList.forEach((ib) => {
          const email = String(ib?.ib_email ?? "").trim();
          if (!email || seenEmails.has(email)) return;

          seenEmails.add(email);
          options.push({
            label: ib?.ib_name ? String(ib.ib_name) : email,
            value: email,
          });
        });

        setIbOptions([{ label: "All", value: "All" }, ...options]);
      })
      .catch((requestError) => {
        if (!alive) return;
        console.error("Unable to load tree IBs for IB selector:", requestError);
        setIbOptionsError("We couldn't load your IBs list.");
        setIbOptions([]);
      })
      .finally(() => {
        if (alive) setIbOptionsLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [type, canUseIbSelector, checkingMasterIb, masterTrees]);

  const individualEmails = useMemo(
    () => ibOptions.filter((option) => option.value !== "All").map((option) => option.value),
    [ibOptions]
  );

  // Trees this user is Master IB of - `FullReportTab` sends these ids as
  // `tree_ids` to POST /ib-report-full, and uses the full tree records
  // (id + description) to build its "Trees" multi-selector.
  const masterTreeIds = useMemo(
    () =>
      (masterTrees || [])
        .map((tree) => tree?.id)
        .filter((treeId) => treeId !== null && treeId !== undefined),
    [masterTrees]
  );

  return {
    type,
    canUseIbSelector,
    ibOptions,
    individualEmails,
    ibOptionsLoading,
    ibOptionsError,
    masterTreeIds,
    masterTrees,
  };
}

export default useIbOptions;